import { LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MapItem } from '@capturum/ui/api';
import { CapturumButtonModule } from '@capturum/ui/button';
import { CapturumCalendarModule } from '@capturum/ui/calendar';
import { CapturumDropdownModule } from '@capturum/ui/dropdown';
import { CapturumMultiSelectModule } from '@capturum/ui/multi-select';
import { CapturumSelectButtonModule } from '@capturum/ui/select-button';
import { GenericFormGroup } from '@core/interfaces/form.interface';
import { NodeEditorCronExpression } from '@features/flow-ui-creator/interfaces/node.interface';
import { FlowService } from '@features/flow-ui-creator/services/flow.service';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { dayOptions, monthDaysOptions, monthsOptions } from '@shared/utils/map-item.util';
import { intervalRegEx } from '@shared/utils/regex.util';
import { CronDate, parseExpression } from 'cron-parser';
import cronstrue from 'cronstrue/i18n';
import { format, isBefore, isSameDay } from 'date-fns';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { SelectChangeEvent as DropdownChangeEvent } from 'primeng/select';
import { KeyFilterModule } from 'primeng/keyfilter';
import { debounceTime, Subscription } from 'rxjs';

enum SchedulerRepeat {
  hourly = 'hourly',
  daily = 'daily',
  weekday = 'weekday',
  weekly = 'weekly',
  monthly = 'monthly',
  yearly = 'yearly',
  advanced = 'advanced',
}

enum ScheduleEventType {
  single,
  recurring,
}

interface CronToken {
  value: string;
  inc: string;
}

@Component({
  selector: 'app-formly-scheduler-type',
  imports: [
    FormlyModule,
    CapturumButtonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    CapturumCalendarModule,
    CapturumDropdownModule,
    CapturumMultiSelectModule,
    CapturumSelectButtonModule,
    NzTimePickerModule,
    KeyFilterModule,
    LowerCasePipe,
  ],
  templateUrl: './formly-scheduler-type.component.html',
  styleUrl: './formly-scheduler-type.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FormlySchedulerTypeComponent extends FieldType implements OnInit, OnDestroy {
  public formGroup: FormGroup<GenericFormGroup<NodeEditorCronExpression>> = new FormGroup({});
  public today = new Date();
  public defaultOpenValue = new Date(0, 0, 0, 0, 0, 0);
  public minuteStep = 15;
  public intervalRegExp = intervalRegEx;

  public schedulerRepeat = SchedulerRepeat;
  public eventType = ScheduleEventType;
  public repeatOptions: MapItem[] = [];
  public dayOptions: MapItem[] = [];
  public monthDaysOptions: MapItem[] = [];
  public endTimeDisabledHours: number[] = [];
  public monthsOptions: MapItem[] = [];
  public eventTypeOptions: MapItem[] = [];
  public numberMonthsOptions: MapItem[] = Array.from({ length: 12 }, (_, i) => {
    return {
      value: i + 1,
      label: `${i + 1}`,
    };
  });

  public timezonesList: MapItem[] = Intl.supportedValuesOf('timeZone').map((timezone) => {
    return {
      value: timezone,
      label: timezone,
    };
  });

  public isCronValid = signal<boolean>(true);
  public cronDescription = signal<string>(null);
  public cronNextExecutionDates = signal<string[]>([]);

  private formSubscription: Subscription;
  private locale = 'en';
  private currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  private schedulerRepeatMapping = {
    [SchedulerRepeat.hourly]: this.computeHourlyCron.bind(this),
    [SchedulerRepeat.daily]: this.computeDailyCron.bind(this),
    [SchedulerRepeat.weekday]: this.computeWorkdaysCron.bind(this),
    [SchedulerRepeat.weekly]: this.computeWeeklyCron.bind(this),
    [SchedulerRepeat.monthly]: this.computeMonthlyCron.bind(this),
    [SchedulerRepeat.yearly]: this.computeYearlyCron.bind(this),
    [SchedulerRepeat.advanced]: this.computeAdvancedCron.bind(this),
  };

  private formBuilder = inject(FormBuilder);
  private translateService = inject(TranslateService);
  private flowService = inject(FlowService);

  constructor() {
    super();

    this.repeatOptions = [
      {
        value: SchedulerRepeat.hourly,
        label: this.translateService.instant('scheduler.repeat.hourly'),
      },
      {
        value: SchedulerRepeat.daily,
        label: this.translateService.instant('scheduler.repeat.daily'),
      },
      {
        value: SchedulerRepeat.weekday,
        label: this.translateService.instant('scheduler.repeat.workdays'),
      },
      {
        value: SchedulerRepeat.weekly,
        label: this.translateService.instant('scheduler.repeat.weekly'),
      },
      {
        value: SchedulerRepeat.monthly,
        label: this.translateService.instant('scheduler.repeat.monthly'),
      },
      {
        value: SchedulerRepeat.advanced,
        label: this.translateService.instant('scheduler.repeat.advanced'),
      },
    ];

    this.eventTypeOptions = [
      {
        value: ScheduleEventType.single,
        label: this.translateService.instant('flow.node.trigger.schedule.single-event.label'),
      },
      {
        value: ScheduleEventType.recurring,
        label: this.translateService.instant('flow.node.trigger.schedule.recurring-event.label'),
      },
    ];

    this.dayOptions = dayOptions(this.translateService, true);
    this.monthsOptions = monthsOptions(this.translateService);
    this.monthDaysOptions = monthDaysOptions(this.translateService);
  }

  get scheduleEventType(): ScheduleEventType {
    return this.formGroup.get('event_type').value;
  }

  get startDate(): Date {
    const startDate = this.formGroup.get('start_date').value;

    return startDate ? new Date(startDate) : null;
  }

  get endDate(): Date {
    const endDate = this.formGroup.get('end_date').value;

    return endDate ? new Date(endDate) : null;
  }

  get startTime(): Date {
    return this.formGroup.get('start_time').value as unknown as Date;
  }

  get endTime(): Date {
    return this.formGroup.get('end_time').value as unknown as Date;
  }

  get selectedRepeat(): SchedulerRepeat {
    return this.formGroup.get('repeat').value as SchedulerRepeat;
  }

  get selectedTimezone(): string {
    return this.formGroup.get('timezone').value;
  }

  public ngOnInit(): void {
    this.locale = this.translateService.currentLang;
    const model = this.field.model?.parameters as NodeEditorCronExpression;
    const startTime = model?.start_time ? this.timeToDateTime(model.start_time) : null;
    const endTime = model?.end_time ? this.timeToDateTime(model.end_time) : null;
    const eventType = this.getSelectedEventType(model);
    const repeatType = this.getSelectedRepeat(model);

    this.formGroup = this.formBuilder.group<GenericFormGroup<NodeEditorCronExpression>>({
      event_type: this.formBuilder.control(eventType),
      start_date: this.formBuilder.control(model?.start_date || null, [Validators.required]),
      end_date: this.formBuilder.control(model?.end_date || null),
      start_time: this.formBuilder.control(startTime, [Validators.required]),
      end_time: this.formBuilder.control(endTime || null),
      hours: this.formBuilder.control(model?.hours || 0),
      minutes: this.formBuilder.control(model?.minutes || 0),
      seconds: this.formBuilder.control(model?.seconds || 0),
      repeat: this.formBuilder.control(repeatType),
      days: this.formBuilder.control(model?.days || []),
      dayOfMonth: this.formBuilder.control(model?.dayOfMonth || 1),
      month: this.formBuilder.control(model?.month || 1),
      timezone: this.formBuilder.control(model?.timezone || this.currentTimezone),
      cron_expression: this.formBuilder.control(model?.cron_expression || null),
    });

    if (model?.cron_expression) {
      this.convertCronToFormValues(model.cron_expression, eventType);
    }

    this.formSubscription = this.formGroup.valueChanges.pipe(debounceTime(100)).subscribe(() => {
      const cronExpression = this.computeCronExpression();

      if (!cronExpression) {
        this.formGroup.patchValue(
          {
            cron_expression: null,
            days: [],
          },
          { emitEvent: false },
        );

        return;
      }

      this.formGroup.get('cron_expression').patchValue(cronExpression, {
        emitEvent: false,
      });

      if (this.formGroup.valid) {
        this.formControl.setValue({
          start_date: this.startDate,
          end_date: this.endDate,
          start_time: `${this.numberToTime(this.startTime.getHours())}:${this.numberToTime(this.startTime.getMinutes())}`,
          end_time: this.endTime
            ? `${this.numberToTime(this.endTime.getHours())}:${this.numberToTime(this.endTime.getMinutes())}`
            : null,
          timezone: this.selectedTimezone,
          cron_expression: cronExpression,
        });
      }

      this.buildCronDescription(cronExpression);
    });

    this.flowService.onTriggerFormlyTypeChange().subscribe(() => {
      this.formGroup.markAllAsTouched();
    });
  }

  public ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  public schedulerRepeatChange(event: DropdownChangeEvent): void {
    this.formGroup.patchValue({
      hours: event.value === SchedulerRepeat.hourly ? 1 : 0,
      minutes: 0,
      seconds: 0,
      days: [],
      dayOfMonth: 1,
      month: 1,
    });
  }

  public eventTypeChange(event: DropdownChangeEvent): void {
    if (
      event.value === this.eventType.recurring &&
      this.formGroup.get('repeat').value === SchedulerRepeat.hourly &&
      !this.formGroup.get('hours').value
    ) {
      this.formGroup.get('hours').patchValue(1);
    }
    this.cronDescription.set(null);
  }

  public startTimeChange(event: Date): void {
    if (!event) {
      this.endTimeDisabledHours = [];
      this.clearEndTime();
    }
  }

  public timePickerOpen(open: boolean, start = false): void {
    const endIsSameDayAsStart = isSameDay(this.startDate, this.endDate);
    const startHours = this.startTime?.getHours();

    if (endIsSameDayAsStart) {
      this.endTimeDisabledHours = startHours ? this.getRange(0, startHours - 1) : [];
    } else {
      this.endTimeDisabledHours = [];
    }

    if (isBefore(this.endTime, this.startTime) && endIsSameDayAsStart) {
      this.clearEndTime();
    }
  }

  public clearEndTime(): void {
    this.formGroup.get('end_time').patchValue(null);
  }

  public disabledEndHours(): number[] {
    return this.endTimeDisabledHours;
  }

  public onFocus(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  public isSingleEvent(): boolean {
    return this.scheduleEventType === ScheduleEventType.single;
  }

  public getSelectedRepeat(model: NodeEditorCronExpression): SchedulerRepeat {
    if (model?.cron_expression) {
      const tokens = model.cron_expression.split(' ');

      // Hourly
      if (tokens[2].includes('/')) {
        return SchedulerRepeat.hourly;
      }

      // Workdays
      if (tokens[5] === '1-5') {
        return SchedulerRepeat.weekday;
      }

      // Weekly
      if (tokens[3] === '?' && (tokens[5] === '*' || tokens[5].includes(',') || Number.isInteger(+tokens[5]))) {
        return SchedulerRepeat.weekly;
      }

      // Monthly
      if (tokens[4].includes('/')) {
        return SchedulerRepeat.monthly;
      }

      // Daily
      if (tokens[0] !== '*' && tokens[1] !== '*' && tokens[2] !== '*' && tokens[5] !== '1-5') {
        return SchedulerRepeat.daily;
      }

      return SchedulerRepeat.advanced;
    }

    return SchedulerRepeat.hourly;
  }

  // CRON Expression
  //<second> <minute> <hour> <day-of-month> <month> <day-of-week> <year>
  private computeCronExpression(): string {
    if (this.scheduleEventType === ScheduleEventType.single) {
      return this.computeSingleEventCron();
    }

    return this.schedulerRepeatMapping[this.formGroup.get('repeat').value]?.();
  }

  private computeSingleEventCron(): string {
    const state = this.formGroup.value;

    if (!state.start_date || !state.start_time) {
      return null;
    }

    const startDate = new Date(state.start_date);
    const startTime = state.start_time as unknown as Date;

    return [
      state.seconds || '0',
      startTime.getMinutes(),
      startTime.getHours(),
      startDate?.getDate(),
      startDate?.getMonth() + 1,
      '?',
      startDate.getFullYear(),
    ].join(' ');
  }

  private computeHourlyCron(): string {
    const state = this.formGroup.value;

    return ['0', state.minutes, `0/${state.hours || 1}`, '*', '*', '?', '*'].join(' ');
  }

  private computeDailyCron(): string {
    const state = this.formGroup.value;

    return [state.seconds || '0', state.minutes, state.hours, '*', '*', '?', '*'].join(' ');
  }

  private computeWorkdaysCron(): string {
    const state = this.formGroup.value;
    const cron = [state.seconds || '0', state.minutes, state.hours, '?', '*', '1-5', '*'].join(' ');

    return cron;
  }

  private computeWeeklyCron(): string {
    const state = this.formGroup.value;
    const days = (state.days as string[]).join(',') || '*';

    return [state.seconds || '0', state.minutes, state.hours, '?', '*', days, '*'].join(' ');
  }

  private computeMonthlyCron(): string {
    const state = this.formGroup.value;

    return [
      state.seconds || '0',
      state.minutes,
      state.hours,
      state.dayOfMonth || '?',
      state.month ? `1/${state.month}` : '*',
      '?',
      '*',
    ].join(' ');
  }

  private computeYearlyCron(): string {
    const state = this.formGroup.value;

    return [
      state.seconds || '0',
      state.minutes,
      state.hours,
      state.dayOfMonth || '?',
      state.month || '*',
      '*',
      '*',
    ].join(' ');
  }

  private computeAdvancedCron(): string {
    const state = this.formGroup.value;

    return state.cron_expression;
  }

  private getRange(start: number, end: number): number[] {
    const length = end - start + 1;

    return [...Array(length)].map((_, i) => {
      return i + start;
    });
  }

  private setCronDescription(cronExpression: string, verbose = true): void {
    const cronDescription = cronstrue.toString(cronExpression, {
      locale: this.locale,
      use24HourTimeFormat: true,
      verbose,
    });
    let dates: string;
    let times: string;

    if (this.startDate) {
      dates = this.translateService.instant('scheduler.recurring-event.dates', {
        start: format(this.startDate, 'dd-MM-yyyy'),
        end: this.endDate ? format(this.endDate, 'dd-MM-yyyy') : undefined,
      });
    }

    if (this.startTime) {
      const start = `${this.numberToTime(this.startTime.getHours())}:${this.numberToTime(this.startTime.getMinutes())}`;
      const end = this.endTime
        ? `${this.numberToTime(this.endTime.getHours())}:${this.numberToTime(this.endTime.getMinutes())}`
        : undefined;

      times = this.translateService.instant('scheduler.recurring-event.times', {
        start,
        end,
      });
    }

    this.cronDescription.set([cronDescription, dates, times].filter(Boolean).join(' '));
  }

  private numberToTime(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private timeToDateTime(time: string | Date): Date {
    if (time instanceof Date) {
      return time;
    }

    const [hours, minutes] = time.split(':');

    return new Date(0, 0, 0, parseInt(hours, 10), parseInt(minutes, 10));
  }

  private convertCronToFormValues(cronExpression: string, eventType: ScheduleEventType): void {
    if (eventType === ScheduleEventType.recurring) {
      const tokens = cronExpression.split(' ');

      // Seconds
      this.formGroup.get('seconds').patchValue(+tokens[0], { emitEvent: false });

      // Minutes
      this.formGroup.get('minutes').patchValue(+tokens[1], { emitEvent: false });

      // Hours
      this.formGroup.get('hours').patchValue(+this.parseCronNumberToken(tokens[2])?.inc, { emitEvent: false });

      // Day of Month
      this.formGroup.get('dayOfMonth').patchValue(+this.parseCronNumberToken(tokens[3])?.inc, { emitEvent: false });

      // Month
      this.formGroup.get('month').patchValue(+this.parseCronNumberToken(tokens[4])?.inc, { emitEvent: false });

      // Days of Week
      if (this.selectedRepeat === SchedulerRepeat.weekly) {
        this.formGroup
          .get('days')
          .patchValue(tokens[5] === '?' || tokens[5] === '*' ? [] : tokens[5].split(',').map(Number), {
            emitEvent: false,
          });
      }

      if (this.selectedRepeat === SchedulerRepeat.weekday) {
        this.formGroup.get('days').patchValue(tokens[5], { emitEvent: false });
      }

      if (this.selectedRepeat === SchedulerRepeat.daily) {
        this.formGroup.get('days').patchValue('?', { emitEvent: false });
      }
    }

    this.buildCronDescription(cronExpression);
  }

  private parseCronNumberToken(value: string): CronToken {
    const val = value.split('/');

    if (val.length === 1) {
      return { value: '0', inc: val[0] };
    }

    return { value: val[0], inc: val[1] };
  }

  private buildCronDescription(cronExpression: string): void {
    try {
      const parsed = parseExpression(cronExpression.slice(0, this.isSingleEvent() ? -5 : -1));

      this.isCronValid.set(true);

      if (this.isSingleEvent()) {
        const hour = this.numberToTime(parsed.fields.hour as unknown as number);
        const minute = this.numberToTime(parsed.fields.minute as unknown as number);
        const description = this.translateService.instant('scheduler.single-event.description', {
          time: `${hour}:${minute}`,
          date: format(this.startDate, 'dd-MM-yyyy'),
        });

        this.cronDescription.set(description);

        return;
      }

      this.setCronDescription(cronExpression);

      const cronNextExecutionDates = parsed.iterate(10).map((date: CronDate) => {
        return format(date.toDate(), 'dd-MM-yyyy HH:mm');
      });

      this.cronNextExecutionDates.set(cronNextExecutionDates);
    } catch (error) {
      this.isCronValid.set(false);
      this.cronDescription.set(null);
    }
  }

  private getSelectedEventType(model: NodeEditorCronExpression): ScheduleEventType {
    if (model?.cron_expression) {
      const tokens = model.cron_expression.split(' ');

      if (tokens[6] === '*') {
        return ScheduleEventType.recurring;
      }

      return ScheduleEventType.single;
    }

    return ScheduleEventType.single;
  }
}
